import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '@/store/api/baseQuery';
import { AppConfig } from '@/constants/residents/config';

export const webPaymentsCompatApi = createApi({
  reducerPath: 'webPaymentsCompatApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['paymentMethods', 'feeStructure', 'autoPayStatus'],
  endpoints: (builder) => ({
    getPaymentMethods: builder.query<any, void>({
      query: () => ({ url: '/api/v3/payments/payment-methods', method: 'GET' }),
      providesTags: ['paymentMethods'],
    }),
    getFeeStructure: builder.query<any, void>({
      query: () => ({ url: '/api/v3/payments/fee-structure', method: 'GET' }),
      providesTags: ['feeStructure'],
    }),
    addBankAccount: builder.mutation<any, { name: string; token: string; isDefault: boolean; accttype: string }>({
      query: (body) => ({ url: '/api/v3/payments/payment-account', method: 'POST', body }),
      invalidatesTags: ['paymentMethods'],
    }),
    addCreditCard: builder.mutation<any, { name: string; token: string; expiry: string; isDefault: boolean }>({
      query: (body) => ({ url: '/api/v3/payments/payment-card', method: 'POST', body }),
      invalidatesTags: ['paymentMethods'],
    }),
    postPayBalance: builder.mutation<any, { amount: number; leaseId: number; sourceId: number; residentId?: number }>({
      query: (body) => ({ url: '/api/v3/payments', method: 'POST', body }),
      invalidatesTags: ['paymentMethods'],
    }),
    setupAutoPay: builder.mutation<any, { 
      leaseId: number; 
      sourceId: number; 
      residentId?: number; 
      autopayIsFullAmount: boolean;
      autopayIsLeaseLifetime: boolean;
      fixedAmount?: number;
      startDate?: string;
      endDate?: string;
    }>({
      query: ({ residentId, sourceId, autopayIsFullAmount, autopayIsLeaseLifetime, fixedAmount, startDate, endDate, leaseId }) => ({
        url: residentId ? `/api/v2/payments/autopay/${residentId}` : '/api/v2/payments/autopay',
        method: 'POST',
        body: {
          sourceId,
          auto_pay_full_amount: autopayIsFullAmount,
          auto_pay_lifetime_of_lease: autopayIsLeaseLifetime,
          leaseId,
          fixed_amount: fixedAmount,
          start_date: startDate,
          end_date: endDate,
        },
      }),
      invalidatesTags: ['autoPayStatus'],
    }),
    cancelAutoPay: builder.mutation<any, number>({
      query: (id) => ({ url: `/api/v2/payments/autopay/${id}`, method: 'DELETE' }),
      invalidatesTags: ['autoPayStatus'],
    }),
    getAutoPayStatus: builder.query<any, void>({
      query: () => ({ url: '/api/v2/payments/autopay', method: 'GET' }),
      providesTags: ['autoPayStatus'],
    }),
    putBankAccount: builder.mutation<any, { sourceId: string | number; bankAccount: { isDefault: boolean } }>({
      query: ({ sourceId, bankAccount }) => ({ url: `/api/v3/payments/payment-account/${sourceId}`, method: 'PUT', body: bankAccount }),
      invalidatesTags: ['paymentMethods'],
    }),
    putCreditCard: builder.mutation<any, { sourceId: string | number; creditCard: { isDefault: boolean } }>({
      query: ({ sourceId, creditCard }) => ({ url: `/api/v3/payments/payment-card/${sourceId}`, method: 'PUT', body: creditCard }),
      invalidatesTags: ['paymentMethods'],
    }),
    deleteBankAccount: builder.mutation<any, { sourceId: string | number }>({
      query: ({ sourceId }) => ({ url: `/api/v3/payments/payment-account/${sourceId}`, method: 'DELETE' }),
      invalidatesTags: ['paymentMethods'],
    }),
    deleteCreditCard: builder.mutation<any, { sourceId: string | number }>({
      query: ({ sourceId }) => ({ url: `/api/v3/payments/payment-card/${sourceId}`, method: 'DELETE' }),
      invalidatesTags: ['paymentMethods'],
    }),
    // CardConnect tokenization (simple implementation matching web)
    generateCardConnectToken: builder.mutation<
      {
        errorcode: number;
        message: string;
        token: string;
      },
      {
        account: string;
        cvv?: string;
        expiry?: string;
      }
    >({
      query: ({ account, cvv, expiry }) => ({
        url: (() => {
          const url = new URL(AppConfig.CARDCONNECT.URL);
          return `https://${url.host}/cardsecure/api/v1/ccn/tokenize`;
        })(),
        method: 'POST',
        body: { account, cvv, expiry },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
  }),
});

export const {
  useGetPaymentMethodsQuery,
  useLazyGetPaymentMethodsQuery,
  useGetFeeStructureQuery,
  useLazyGetFeeStructureQuery,
  useAddBankAccountMutation,
  useAddCreditCardMutation,
  usePostPayBalanceMutation,
  useSetupAutoPayMutation,
  useCancelAutoPayMutation,
  useGetAutoPayStatusQuery,
  usePutBankAccountMutation,
  usePutCreditCardMutation,
  useDeleteBankAccountMutation,
  useDeleteCreditCardMutation,
  useGenerateCardConnectTokenMutation,
} = webPaymentsCompatApi;


